import axios from "axios";
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { getCurrentUsername } from "../utils/auth";

const API_URL = process.env.REACT_APP_API_URL || process.env.REACT_APP_LOCAL_API_URL;

function authHeaders() {
  return {
    "Content-Type": "application/json",
    token: localStorage.getItem("token")
  };
}

function MyInvitations() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const highlightedInvitationId = searchParams.get("invitation") || "";
  const [invitations, setInvitations] = useState([]);
  const [memberGroups, setMemberGroups] = useState([]);
  const [statusMessage, setStatusMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const username = getCurrentUsername();

  const fetchData = useCallback(async () => {
    try {
      const [invitationResponse, groupResponse] = await Promise.all([
        axios.get(`${API_URL}/invitations`, { headers: authHeaders() }),
        axios.get(`${API_URL}/groups/my-groups`, { headers: authHeaders() })
      ]);

      const invitations = invitationResponse.data.invitations || [];
      setInvitations(invitations);
      setMemberGroups(groupResponse.data.memberGroups || []);
      window.dispatchEvent(new CustomEvent("invitations-updated", {
        detail: {
          pendingCount: invitations.filter((invitation) => invitation.effectiveStatus === "pending").length
        }
      }));
      setErrorMessage("");
    } catch (error) {
      setErrorMessage(error.response?.data?.message || "Kunde inte hämta inbjudningar");
    }
  }, []);

  async function respondToInvitation(invitationId, action) {
    try {
      const response = await axios.post(`${API_URL}/invitations/${invitationId}/${action}`, {}, { headers: authHeaders() });
      setStatusMessage(response.data.message || "Inbjudan uppdaterades.");
      setErrorMessage("");
      await fetchData();
    } catch (error) {
      setErrorMessage(error.response?.data?.message || "Kunde inte uppdatera inbjudan");
      setStatusMessage("");
    }
  }

  async function leaveGroup(groupId) {
    if (!window.confirm("Lämna gruppen? Du förlorar åtkomst till gruppens delade pass.")) {
      return;
    }

    try {
      const response = await axios.delete(`${API_URL}/groups/${groupId}/leave`, { headers: authHeaders() });
      setStatusMessage(response.data.message || "Du har lämnat gruppen.");
      setErrorMessage("");
      await fetchData();
    } catch (error) {
      setErrorMessage(error.response?.data?.message || "Kunde inte lämna gruppen");
      setStatusMessage("");
    }
  }

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (!highlightedInvitationId || !invitations.length) return;
    document.getElementById(`invitation-${highlightedInvitationId}`)?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [highlightedInvitationId, invitations]);

  const pendingInvitations = invitations.filter((invitation) => invitation.effectiveStatus === "pending");
  const expiredInvitations = invitations.filter((invitation) => invitation.effectiveStatus === "expired");

  return (
    <div className="flex flex-col items-center px-3">
      <div className="w-full max-w-4xl py-4">
        <div className="theme-card">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-sky-700">Grupper</p>
              <h1 className="mt-2 text-lg font-bold text-slate-800">Inbjudningar</h1>
              <p className="mt-2 text-sm text-slate-600">
                Acceptera gruppinbjudningar och se vilka grupper du är medlem i.
              </p>
            </div>
            <button
              className="rounded-[20px] border border-slate-200 bg-white px-5 py-3 text-sm font-semibold text-slate-700 shadow-sm transition-colors hover:bg-slate-100"
              onClick={() => navigate("/mypage")}
            >
              Till meny
            </button>
          </div>

          {statusMessage && <div className="theme-alert-success mt-4 py-3">{statusMessage}</div>}
          {errorMessage && <div className="theme-alert-error mt-4 py-3">{errorMessage}</div>}

          <section className="mt-5">
            <p className="text-sm font-semibold text-slate-800">Väntande inbjudningar</p>
            <div className="mt-3 space-y-3">
              {pendingInvitations.length === 0 && (
                <div className="theme-empty-state">Du har inga väntande inbjudningar.</div>
              )}
              {pendingInvitations.map((invitation) => (
                <div id={`invitation-${invitation._id}`} key={invitation._id} className={`theme-panel ${highlightedInvitationId === invitation._id ? "border-sky-500 ring-2 ring-sky-200" : ""}`}>
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                      <p className="font-semibold text-slate-800">{invitation.group?.name || "Grupp"}</p>
                      <p className="mt-1 text-sm text-slate-600">Inbjuden av {invitation.invitedByName || "Gruppansvarig"} · {invitation.assignedRole === 5 ? "Tränare" : "Utövare"}</p>
                      {invitation.group?.description && <p className="mt-1 text-sm text-slate-600">{invitation.group.description}</p>}
                      <p className="mt-2 text-xs font-medium text-slate-500">Gäller till {new Date(invitation.expiresAt).toLocaleDateString("sv-SE")}</p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <button
                        className="theme-badge-button border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                        onClick={() => respondToInvitation(invitation._id, "accept")}
                      >
                        Acceptera
                      </button>
                      <button
                        className="theme-badge-button border-red-200 text-red-700 hover:bg-red-50"
                        onClick={() => respondToInvitation(invitation._id, "reject")}
                      >
                        Avslå
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section className="mt-6">
            <p className="text-sm font-semibold text-slate-800">Utgångna inbjudningar</p>
            <div className="mt-3 space-y-3">
              {expiredInvitations.length === 0 && (
                <div className="theme-empty-state">Du har inga utgångna inbjudningar.</div>
              )}
              {expiredInvitations.map((invitation) => (
                <div id={`invitation-${invitation._id}`} key={invitation._id} className={`rounded-[24px] border border-amber-200 bg-amber-50 px-5 py-4 ${highlightedInvitationId === invitation._id ? "ring-2 ring-amber-300" : ""}`}>
                  <p className="font-semibold text-slate-800">{invitation.group?.name || "Grupp"}</p>
                  <p className="mt-1 text-sm text-slate-600">Inbjuden av {invitation.invitedByName || "Gruppansvarig"} · {invitation.assignedRole === 5 ? "Tränare" : "Utövare"}</p>
                  <p className="mt-2 text-xs font-semibold text-amber-700">Gick ut {new Date(invitation.expiresAt).toLocaleDateString("sv-SE")}</p>
                  <p className="mt-2 text-sm text-slate-600">Kontakta gruppadministratören för att få inbjudan omskickad.</p>
                </div>
              ))}
            </div>
          </section>

          <section className="mt-6">
            <p className="text-sm font-semibold text-slate-800">Mina grupper</p>
            <div className="mt-3 space-y-3">
              {memberGroups.length === 0 && (
                <div className="theme-empty-state">Du är inte medlem i någon grupp.</div>
              )}
              {memberGroups.map((group) => (
                <div key={group._id} className="theme-panel">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                      <p className="font-semibold text-slate-800">{group.name}</p>
                      <p className="mt-1 text-sm text-slate-600">Administratör: {group.administratorName || "Okänd administratör"}</p>
                    </div>
                    {group.administratorUsername !== username && (
                      <button
                        className="theme-badge-button border-red-200 text-red-700 hover:bg-red-50"
                        onClick={() => leaveGroup(group._id)}
                      >
                        Lämna grupp
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}

export default MyInvitations;
